// src/layout/customer/Payment/CustomerPaymentRequest.jsx
import React, { useState, useEffect } from "react";
import {
  HelpCircle,
  CheckCircle,
  XCircle,
  Clock,
  IndianRupee,
  Send,
  MessageSquare,
  Loader2,
} from "lucide-react";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import useCustomerPaymentStore from "../../../store/customer/useCustomerPaymentStore";

const formatDateTime = (isoDateString) => {
  if (!isoDateString) return "N/A";
  try {
    return new Date(isoDateString)
      .toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(",", "");
  } catch (e) {
    return "Invalid Date";
  }
};

// Status of the request itself (e.g., pending customer, approved, rejected)
const requestStatusDisplayInfo = (requestStatus) => {
  switch (requestStatus) {
    case "Approved by Customer":
      return {
        icon: <CheckCircle className="w-3.5 h-3.5 text-green-500" />,
        text: "You Accepted",
        bgClass: "bg-green-100 text-green-700",
      };
    case "Pending Customer Action":
      return {
        icon: <Clock className="w-3.5 h-3.5 text-yellow-500" />,
        text: "Pending Your Action",
        bgClass: "bg-yellow-100 text-yellow-700",
      };
    case "Rejected by Customer":
      return {
        icon: <XCircle className="w-3.5 h-3.5 text-red-500" />,
        text: "You Declined",
        bgClass: "bg-red-100 text-red-700",
      };
    default:
      return {
        icon: <HelpCircle className="w-3.5 h-3.5 text-gray-500" />,
        text: requestStatus || "Unknown",
        bgClass: "bg-gray-100 text-gray-700",
      };
  }
};

// Status of the payment related to the request
const paymentStatusDisplayInfo = (paymentStatus) => {
  switch (paymentStatus) {
    case "Paid":
      return {
        icon: <CheckCircle className="w-3.5 h-3.5 text-green-600" />,
        text: "Paid",
        textClass: "text-green-600",
      };
    case "Payment Pending":
      return {
        icon: <Clock className="w-3.5 h-3.5 text-orange-500" />,
        text: "Payment Pending",
        textClass: "text-orange-600",
      };
    case "Not Applicable": // For rejected requests
      return {
        icon: <HelpCircle className="w-3.5 h-3.5 text-gray-400" />,
        text: "Not Applicable",
        textClass: "text-gray-500",
      };
    default:
      return {
        icon: <HelpCircle className="w-3.5 h-3.5 text-gray-400" />,
        text: paymentStatus || "N/A",
        textClass: "text-gray-500",
      };
  }
};

const CustomerPaymentRequest = ({ projectId, compact = false }) => {
  const {
    paymentRequests, // Fetched from builder's perspective via GET /projects/{projectId}/builder-payment-requests
    isRequestsLoading,
    isActionSubmitting, // Loading state for accept/decline actions
    fetchPaymentRequestsForCustomer,
    respondToPaymentRequest, // Customer's action: accept/decline
  } = useCustomerPaymentStore();

  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentRequestForAction, setCurrentRequestForAction] = useState(null);

  const [confirmPopup, setConfirmPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => { },
    isLoading: false, // This will be tied to isActionSubmitting
    actionType: "", // 'accept' or 'decline'
  });

  useEffect(() => {
    if (projectId) {
      fetchPaymentRequestsForCustomer(projectId);
    }
  }, [projectId, fetchPaymentRequestsForCustomer]);

  // Debug: Log payment requests to see the data structure
  useEffect(() => {
    if (paymentRequests.length > 0) {
      console.log("Payment Requests Data:", paymentRequests);
      paymentRequests.forEach((request, index) => {
        console.log(`Request ${index + 1}:`, {
          id: request.id,
          status: request.status,
          payment_status: request.payment_status,
          amount: request.amount,
          comment: request.comment
        });
      });
    }
  }, [paymentRequests]);

  const handleOpenRejectionModal = (request) => {
    setCurrentRequestForAction(request);
    setRejectionModalOpen(true);
    setRejectionReason(""); // Clear previous reason
  };

  const handleCloseRejectionModal = () => {
    setRejectionModalOpen(false);
    setCurrentRequestForAction(null);
    setRejectionReason("");
  };

  // This is triggered when user confirms "Decline" from the Rejection Reason Modal
  const handleDeclineSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please provide a reason for declining.");
      return;
    }
    setConfirmPopup({
      isOpen: true,
      title: "Confirm Decline",
      message:
        "Are you sure you want to decline this payment request? This action will be recorded.",
      actionType: "decline",
      onConfirm: async () => {
        // This onConfirm will call respondToPaymentRequest
        setConfirmPopup((prev) => ({ ...prev, isLoading: true })); // Use confirmPopup's isLoading
        const result = await respondToPaymentRequest(
          projectId,
          currentRequestForAction.id,
          "decline",
          rejectionReason
        );
        if (result.success) {
          handleCloseRejectionModal(); // Close reason modal on success
        }
        // Error toast is handled by the store
        setConfirmPopup({ isOpen: false, isLoading: false }); // Reset confirmation popup
      },
      isLoading: isActionSubmitting, // Driven by store's action submitting state
    });
  };

  // This is triggered when user clicks "Accept & Pay"
  const handleAccept = (request) => {
    setCurrentRequestForAction(request);
    setConfirmPopup({
      isOpen: true,
      title: "Confirm Acceptance",
      message: `You are about to approve a payment request of ₹${parseFloat(
        request.amount
      ).toLocaleString(
        "en-IN"
      )}. After approval, the builder may proceed with payment collection.`,
      actionType: "accept",
      onConfirm: async () => {
        // This onConfirm will call respondToPaymentRequest
        setConfirmPopup((prev) => ({ ...prev, isLoading: true })); // Use confirmPopup's isLoading
        const result = await respondToPaymentRequest(
          projectId,
          request.id,
          "accept"
        );
        // Error toast is handled by the store
        setConfirmPopup({ isOpen: false, isLoading: false }); // Reset confirmation popup
        // If successful, UI will update via fetchPaymentRequestsForCustomer
      },
      isLoading: isActionSubmitting, // Driven by store's action submitting state
    });
  };

  if (isRequestsLoading && paymentRequests.length === 0) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader2 className="animate-spin text-2xl text-blue-500" />
        <p className="ml-2 text-gray-500">Loading payment requests...</p>
      </div>
    );
  }

  if (!isRequestsLoading && paymentRequests.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 bg-white rounded-lg shadow p-6">
        <IndianRupee className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <p className="text-lg font-semibold">
          No Payment Requests from Builder
        </p>
        <p className="text-sm">
          Your builder has not sent any payment requests yet for this project.
        </p>
      </div>
    );
  }

  if (compact) {
    // --- COMPACT TABLE-LIKE VIEW WITH MOBILE RESPONSIVENESS ---
    return (
      <>
        {/* Desktop Table View - Hidden on mobile */}
        <div className="hidden md:block rounded-lg overflow-hidden border border-gray-200">
          <div className="bg-white px-6 py-3 border-b border-gray-200 flex items-center justify-between">
            <span className="font-semibold text-base text-gray-700">Builder Payment Requests</span>
          </div>
          <div className="grid grid-cols-[180px_1fr_160px_200px] items-center bg-gray-50 px-6 py-3 font-semibold text-xs text-gray-700 border-b border-gray-200">
            <span>Date & Time</span>
            <span>Comment</span>
            <span className="justify-end flex">Amount</span>
            <span className="justify-end flex">Status / Action</span>
          </div>
          {/* Data Rows */}
          {paymentRequests.length === 0 ? (
            <div className="col-span-4 text-center py-8 text-gray-500 bg-white">No Payment Requests from Builder</div>
          ) : (
            paymentRequests.map((request, index) => {
              const canTakeAction = request.status === "Pending Customer Action";
              const reqStatusInfo = requestStatusDisplayInfo(request.status);
              const payStatusInfo = paymentStatusDisplayInfo(request.payment_status);
              return (
                <div key={request.id || index} className="grid grid-cols-[180px_1fr_160px_200px] items-center bg-white px-6 py-3 border-b border-gray-100 text-sm font-medium">
                  {/* Date/Time */}
                  <span className="text-gray-600 flex items-center"><Clock className="w-3 h-3 mr-1 text-gray-400" />{formatDateTime(request.request_date)}</span>
                  {/* Comment */}
                  <span className="text-gray-800 truncate">{request.comment || "No comment"}</span>
                  {/* Amount */}
                  <span className="font-semibold text-gray-900 flex items-center justify-end text-base"><IndianRupee className="w-4 h-4 mr-1 text-gray-700" />{parseFloat(request.amount).toLocaleString("en-IN")}</span>
                  {/* Accept/Reject or Status */}
                  <span className="flex justify-end items-center">
                    {canTakeAction ? (
                      <>
                        <button
                          onClick={() => handleAccept(request)}
                          disabled={isActionSubmitting}
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs font-semibold flex items-center gap-1 transition-colors duration-200 disabled:opacity-70"
                        >
                          {isActionSubmitting && confirmPopup.actionType === "accept" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                          Accept
                        </button>
                        <button
                          onClick={() => handleOpenRejectionModal(request)}
                          disabled={isActionSubmitting}
                          className="ml-2 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-semibold flex items-center gap-1 transition-colors duration-200 disabled:opacity-70"
                        >
                          {isActionSubmitting && confirmPopup.actionType === "decline" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                          Reject
                        </button>
                      </>
                    ) : request.status === "Approved by Customer" && (request.payment_status === "Paid" || request.payment_status === "paid") ? (
                      <span className="flex items-center text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded">
                        <CheckCircle className="w-3 h-3 mr-1 text-green-500" /> Paid
                      </span>
                    ) : request.status === "Approved by Customer" && (request.payment_status === "Payment Pending" || request.payment_status === "payment_pending" || !request.payment_status) ? (
                      <span className="flex items-center text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded">
                        <Clock className="w-3 h-3 mr-1 text-orange-500" /> Payment Pending
                      </span>
                    ) : request.status === "Rejected by Customer" ? (
                      <span className="flex items-center text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">
                        <XCircle className="w-3 h-3 mr-1 text-red-500" /> You Declined
                      </span>
                    ) : (
                      <span className="flex items-center text-xs font-semibold text-gray-600 bg-gray-50 px-2 py-1 rounded">
                        <HelpCircle className="w-3 h-3 mr-1 text-gray-500" /> {request.status || "Unknown"}
                      </span>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Mobile Card View - Shown only on mobile */}
        <div className="md:hidden space-y-3">
          {paymentRequests.length === 0 ? (
            <div className="text-center py-8 text-gray-500 bg-white rounded-lg shadow p-6">
              <IndianRupee className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-base font-semibold">No Payment Requests from Builder</p>
              <p className="text-xs mt-1">
                Your builder has not sent any payment requests yet for this project.
              </p>
            </div>
          ) : (
            paymentRequests.map((request, index) => {
              const canTakeAction = request.status === "Pending Customer Action";
              const reqStatusInfo = requestStatusDisplayInfo(request.status);
              const payStatusInfo = paymentStatusDisplayInfo(request.payment_status);

              return (
                <div
                  key={request.id || index}
                  className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm"
                >
                  {/* Date & Amount Row */}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center text-xs text-gray-600">
                      <Clock className="w-3 h-3 mr-1 text-gray-400" />
                      {formatDateTime(request.request_date)}
                    </div>
                    <div className="font-semibold text-gray-900 flex items-center text-sm">
                      <IndianRupee className="w-3.5 h-3.5 mr-0.5 text-gray-700" />
                      {parseFloat(request.amount).toLocaleString("en-IN")}
                    </div>
                  </div>

                  {/* Comment */}
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">Comment:</p>
                    <p className="text-xs text-gray-800">{request.comment || "No comment"}</p>
                  </div>

                  {/* Status Badge */}
                  <div className="mb-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${reqStatusInfo.bgClass}`}
                    >
                      {reqStatusInfo.icon}
                      <span className="ml-1.5">{reqStatusInfo.text}</span>
                    </span>
                  </div>

                  {/* Action Buttons or Payment Status */}
                  <div className="pt-2 border-t border-gray-100">
                    {canTakeAction ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAccept(request)}
                          disabled={isActionSubmitting}
                          className="flex-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 disabled:opacity-70"
                        >
                          {isActionSubmitting && confirmPopup.actionType === "accept" ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <CheckCircle size={14} />
                          )}
                          Accept
                        </button>
                        <button
                          onClick={() => handleOpenRejectionModal(request)}
                          disabled={isActionSubmitting}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-md text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors duration-200 disabled:opacity-70"
                        >
                          {isActionSubmitting && confirmPopup.actionType === "decline" ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <XCircle size={14} />
                          )}
                          Reject
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs">
                        <span className="text-gray-500 block mb-1">Payment Status:</span>
                        {request.status === "Approved by Customer" && (request.payment_status === "Paid" || request.payment_status === "paid") ? (
                          <span className="flex items-center font-semibold text-green-600">
                            <CheckCircle className="w-3 h-3 mr-1" /> Paid
                          </span>
                        ) : request.status === "Approved by Customer" && (request.payment_status === "Payment Pending" || request.payment_status === "payment_pending" || !request.payment_status) ? (
                          <span className="flex items-center font-semibold text-orange-600">
                            <Clock className="w-3 h-3 mr-1" /> Payment Pending
                          </span>
                        ) : request.status === "Rejected by Customer" ? (
                          <span className="flex items-center font-semibold text-red-600">
                            <XCircle className="w-3 h-3 mr-1" /> You Declined
                          </span>
                        ) : (
                          <span className="flex items-center font-semibold text-gray-600">
                            <HelpCircle className="w-3 h-3 mr-1" /> {request.status || "Unknown"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {/* Rejection Modal, Confirmation Popup */}
        <AnimatePresence>
          {rejectionModalOpen && currentRequestForAction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4"
              onClick={handleCloseRejectionModal}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.9, y: 20, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  Decline Payment Request
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Provide a reason for declining. This will be shared with the
                  builder.
                </p>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for declining..."
                  rows={4}
                  className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-300 mb-4"
                  disabled={isActionSubmitting}
                />
                <div className="flex justify-end gap-3">
                  <button
                    onClick={handleCloseRejectionModal}
                    disabled={isActionSubmitting}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-xs font-medium transition-colors disabled:opacity-70"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeclineSubmit}
                    disabled={!rejectionReason.trim() || isActionSubmitting}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                  >
                    {isActionSubmitting &&
                      confirmPopup.actionType === "decline" ? (
                      <Loader2 className="animate-spin w-4 h-4" />
                    ) : (
                      <Send size={14} />
                    )}
                    Submit Decline
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <ConfirmationPopup
          isOpen={confirmPopup.isOpen}
          onClose={() => setConfirmPopup((prev) => ({ ...prev, isOpen: false }))}
          onConfirm={confirmPopup.onConfirm}
          title={confirmPopup.title}
          message={confirmPopup.message}
          isLoading={confirmPopup.isLoading}
          confirmText={
            confirmPopup.actionType === "accept" ? "Yes, Approve" : "Yes, Decline"
          }
        />
      </>
    );
  }

  // --- ORIGINAL CARD (non-compact) ---
  return (
    <>
      <div className={compact ? "space-y-3" : "space-y-8"}>
        {paymentRequests.map((request, index) => {
          const canTakeAction = request.status === "Pending Customer Action";
          const reqStatusInfo = requestStatusDisplayInfo(request.status);
          const payStatusInfo = paymentStatusDisplayInfo(request.payment_status);

          return (
            <div
              key={request.id || index}
              className="relative pl-10 py-2 group"
            >
              <div
                className={`absolute left-0 top-2.5 w-4 h-4 rounded-full border-2 border-white shadow-lg z-10 
                ${reqStatusInfo.bgClass
                    .replace("bg-", "ring-")
                    .replace("-100", "-300")} ${reqStatusInfo.bgClass.replace(
                      "-100",
                      "-500"
                    )}
                ${canTakeAction ? "animate-pulse ring-yellow-400" : ""}`}
              >
                {" "}
                {/* Pulse if action needed */}
              </div>
              {index !== paymentRequests.length - 1 && (
                <div
                  className={`absolute left-[7px] top-[18px] h-full w-[2px] transition-colors 
                  ${reqStatusInfo.bgClass.replace(
                    "-100",
                    "-200"
                  )} group-hover:bg-opacity-75`}
                ></div>
              )}

              <div className="p-5 bg-white rounded-lg shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3">
                  <div className="mb-2 sm:mb-0">
                    <p className="text-xs text-gray-500 flex items-center">
                      <Clock className="w-3.5 h-3.5 mr-1.5 text-gray-400" />
                      Requested: {formatDateTime(request.request_date)}
                    </p>
                    <h4 className="text-base font-semibold text-gray-800 mt-1">
                      Payment Request from Builder
                    </h4>
                  </div>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${reqStatusInfo.bgClass}`}
                  >
                    {reqStatusInfo.icon}
                    <span className="ml-1.5">{reqStatusInfo.text}</span>
                  </span>
                </div>

                <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                  {request.comment}
                </p>

                <div className="grid grid-cols-2 gap-3 text-xs mb-4 border-t border-b border-gray-100 py-3">
                  <div>
                    <p className="text-gray-500">Amount Requested</p>
                    <p className="font-semibold text-gray-800 flex items-center mt-0.5 text-base">
                      <IndianRupee className="w-4 h-4 mr-1 text-gray-500" />{" "}
                      {parseFloat(request.amount).toLocaleString("en-IN")}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Payment Status</p>
                    <p
                      className={`font-semibold flex items-center mt-0.5 ${payStatusInfo.textClass}`}
                    >
                      {payStatusInfo.icon}{" "}
                      <span className="ml-1">{payStatusInfo.text}</span>
                    </p>
                  </div>
                </div>

                {request.status === "Rejected by Customer" &&
                  request.customer_reason && (
                    <div className="mb-4 p-3 bg-red-50 rounded-md border border-red-100">
                      <p className="text-xs font-medium text-red-700 flex items-center">
                        <MessageSquare size={14} className="mr-1.5" />
                        Your Reason for Declining:
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        {request.customer_reason}
                      </p>
                      {/* Optionally show date of rejection if available from API */}
                      {/* {request.customerActionDate && <p className="text-[10px] text-red-400 mt-1">Declined on: {formatDateTime(request.customerActionDate)}</p>} */}
                    </div>
                  )}

                {request.status === "Approved by Customer" &&
                  request.payment_status === "Paid" &&
                  request.updated_at && (
                    <div className="mb-4 p-3 bg-green-50 rounded-md border border-green-100 text-xs">
                      <p className="text-green-700 font-medium">
                        Payment confirmed on:{" "}
                        {formatDateTime(request.updated_at)}
                      </p>
                    </div>
                  )}

                {canTakeAction && (
                  <div className="flex flex-col sm:flex-row gap-3 mt-4 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => handleAccept(request)}
                      disabled={isActionSubmitting}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-xs font-semibold flex items-center justify-center gap-2 transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-70"
                    >
                      {isActionSubmitting &&
                        confirmPopup.actionType === "accept" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Accept & Proceed
                    </button>
                    <button
                      onClick={() => handleOpenRejectionModal(request)}
                      disabled={isActionSubmitting}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md text-xs font-semibold flex items-center justify-center gap-2 transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-70"
                    >
                      {isActionSubmitting &&
                        confirmPopup.actionType === "decline" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      Decline Request
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {rejectionModalOpen && currentRequestForAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[70] p-4"
            onClick={handleCloseRejectionModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                Decline Payment Request
              </h3>
              <p className="text-xs text-gray-500 mb-4">
                Provide a reason for declining. This will be shared with the
                builder.
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter reason for declining..."
                rows={4}
                className="w-full p-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-300 mb-4"
                disabled={isActionSubmitting}
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseRejectionModal}
                  disabled={isActionSubmitting}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-xs font-medium transition-colors disabled:opacity-70"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeclineSubmit}
                  disabled={!rejectionReason.trim() || isActionSubmitting}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
                >
                  {isActionSubmitting &&
                    confirmPopup.actionType === "decline" ? (
                    <Loader2 className="animate-spin w-4 h-4" />
                  ) : (
                    <Send size={14} />
                  )}
                  Submit Decline
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmationPopup
        isOpen={confirmPopup.isOpen}
        onClose={() => setConfirmPopup((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmPopup.onConfirm}
        title={confirmPopup.title}
        message={confirmPopup.message}
        isLoading={confirmPopup.isLoading} // This is now driven by isActionSubmitting from store
        confirmText={
          confirmPopup.actionType === "accept" ? "Yes, Approve" : "Yes, Decline"
        }
      />
    </>
  );
};

export default CustomerPaymentRequest;
